import { SOCKET_EVENT_NAMES } from '@/shared/socket/types';
import {
  WAHA_SOCKET_MESSAGE_TYPE,
  type ParsedWahaSocketMessage,
  type WahaSessionStatusSocketMessage,
} from '@/shared/socket/waha/types';

const SOCKET_EVENT_NAME_SET = new Set<string>(SOCKET_EVENT_NAMES);

const PROTOCOL_FRAME_TYPES = new Set([
  'health',
  'pong',
  'ping',
  'heartbeat',
  'alive',
  'ok',
  'subscribed',
  'unsubscribed',
  'subscribe',
  'unsubscribe',
  'ack',
  'error',
]);

function parseJson(body: string): unknown {
  if (!body.trim()) {
    return null;
  }

  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function isWahaSessionStatusMessage(
  value: unknown,
): value is WahaSessionStatusSocketMessage {
  if (!isRecord(value)) {
    return false;
  }

  if (value.type !== WAHA_SOCKET_MESSAGE_TYPE) {
    return false;
  }

  const payload = value.payload;
  if (!isRecord(payload)) {
    return false;
  }

  return (
    typeof payload.sessionName === 'string' &&
    typeof payload.engineStatus === 'string'
  );
}

function unwrapWahaChannelPayload(
  value: Record<string, unknown>,
): Record<string, unknown> {
  const channel = typeof value.channel === 'string' ? value.channel.trim() : '';
  const payload = value.payload;

  if (channel.startsWith('branch:') && isRecord(payload)) {
    return payload;
  }

  return value;
}

function readUpdateType(value: Record<string, unknown>): string {
  return typeof value.updateType === 'string' ? value.updateType.trim() : '';
}

/** Booking / planning / table frames share the legacy STOMP body shape. */
function isBookingRealtimeFrame(value: Record<string, unknown>): boolean {
  const body = unwrapWahaChannelPayload(value);

  if (isRecord(body.bookingDTO)) {
    return true;
  }

  if (isRecord(body.tableDTO) || isRecord(body.roomDTO)) {
    return true;
  }

  const updateType = readUpdateType(body);
  if (updateType && SOCKET_EVENT_NAME_SET.has(updateType)) {
    return true;
  }

  return false;
}

function isProtocolFrame(value: Record<string, unknown>): boolean {
  const frameType =
    typeof value.type === 'string' ? value.type.trim().toLowerCase() : '';

  if (!frameType) {
    return false;
  }

  return PROTOCOL_FRAME_TYPES.has(frameType) || frameType.includes('health');
}

export function parseWahaSocketBody(rawBody: string): ParsedWahaSocketMessage {
  const parsedBody = parseJson(rawBody);

  if (!isRecord(parsedBody)) {
    return {
      rawBody,
      sessionMessage: null,
      isBookingRealtimeEvent: false,
    };
  }

  if (isProtocolFrame(parsedBody)) {
    return {
      rawBody,
      sessionMessage: null,
      isBookingRealtimeEvent: false,
    };
  }

  const sessionMessage = isWahaSessionStatusMessage(parsedBody)
    ? parsedBody
    : null;
  const bookingRealtimeBody =
    !sessionMessage && isBookingRealtimeFrame(parsedBody)
      ? unwrapWahaChannelPayload(parsedBody)
      : undefined;

  return {
    rawBody,
    sessionMessage,
    isBookingRealtimeEvent: Boolean(bookingRealtimeBody),
    bookingRealtimeBody,
  };
}
